/** @jsx jsx */
import { useEffect, useState } from 'react';
import { jsx, css } from '@emotion/core';
import { Colors } from '@zapier/theme';

type ConfettiPiece = {
  color: string;
  width: number;
  height: number;
  top: number;
  left: number;
  transform: number;
  opacity: number;
  transition?: string;
};

const createSingleConfetti = (): ConfettiPiece => {
  const width = Math.random() * 14;
  const height = width * 0.7;
  const colorIdx = Math.ceil(Math.random() * 4);
  let color;
  switch (colorIdx) {
    case 1:
      color = Colors.ramona;
      break;
    case 2:
      color = Colors.heroine;
      break;
    case 3:
      color = Colors.viper;
      break;
    default:
      color = Colors.wolverine;
  }

  return {
    color,
    width,
    height,
    top: -Math.random() * 20 - 2.5,
    left: Math.random() * 100,
    transform: Math.random() * 360,
    opacity: Math.random() + 0.8,
  };
};

const containerStyles = css`
  pointer-events: none;
`;

const confettiStyles = (piece: ConfettiPiece) => {
  return css`
    will-change: top, left;
    background-color: ${piece.color};
    width: ${piece.width}px;
    height: ${piece.height}px;
    top: ${piece.top}%;
    left: ${piece.left}%;
    transform: rotate(${piece.transform}deg);
    position: absolute;
    opacity: ${piece.opacity};
    transition: ${piece.transition};
  `;
};

const createConfetti = (numOfPieces: number) => {
  let confettiPieces = [] as ConfettiPiece[];

  for (let i = 0; i < numOfPieces; i++) {
    const confetti = createSingleConfetti();
    confettiPieces = [confetti, ...confettiPieces];
  }

  return confettiPieces;
};

type Props = {
  shouldLoop?: boolean;
  numOfPieces?: number;
};

const setConfettiAnimation = (confettiPieces: ConfettiPiece[]) => {
  const easingFunc = 'cubic-bezier(.02, .01, .47, 1)';

  const droppedConfetti = confettiPieces.map(confetti => {
    const randomBinaryNum = Math.floor(Math.random() * 2);
    const randomizeLeft =
      randomBinaryNum === 0
        ? confetti.left + Math.random() * 15
        : confetti.left - Math.random() * 15;

    confetti.top = 105;
    confetti.left = randomizeLeft;
    confetti.transition = `top ${Math.random() * 3 + 2}s ${easingFunc}, left ${
      Math.random() * 3 + 2
    }s ${easingFunc}`;

    return confetti;
  });

  return droppedConfetti;
};

export const Confetti = ({ numOfPieces = 150, shouldLoop = true }: Props) => {
  const [confettiPieces, setConfettiPieces] = useState(
    createConfetti(numOfPieces)
  );

  useEffect(() => {
    let resetInterval: NodeJS.Timeout;
    let dropInterval: NodeJS.Timeout;

    const resetConfetti = () => {
      setConfettiPieces(createConfetti(numOfPieces));
    };
    const dropConfetti = () => {
      setConfettiPieces(s => setConfettiAnimation(s));
    };

    dropConfetti();

    if (shouldLoop) {
      resetInterval = setInterval(resetConfetti, 5000);
      setTimeout(() => {
        dropInterval = setInterval(dropConfetti, 5000);
      }, 0);
    }

    return () => {
      clearInterval(dropInterval);
      clearInterval(resetInterval);
    };
  }, [numOfPieces, shouldLoop]);

  return (
    <div css={containerStyles}>
      {confettiPieces.map((piece, i) => {
        return <div css={confettiStyles(piece)} key={i} />;
      })}
    </div>
  );
};
